import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Layers } from 'lucide-react';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { LayerUIConfig } from './types';

interface LayerControlProps {
	layers: LayerUIConfig[];
	onLayerToggle: (layerId: string, visible: boolean) => void;
}

export function LayerControl({ layers, onLayerToggle }: LayerControlProps) {
	const [isOpen, setIsOpen] = useState(false);
	const { isDesktop } = useIsDesktopSize();

	const visibleLayersCount = layers.filter(layer => layer.visible).length;

	if (isDesktop) {
		return (
			<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						size="sm"
						className="h-8 px-3 bg-white/50 dark:bg-slate-800/50 hover:bg-white/80 dark:hover:bg-slate-800/80"
					>
						<Layers className="h-3 w-3 mr-1" />
						Layers ({visibleLayersCount})
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-56 p-2">
					{layers.map(({ Icon, ...layer }) => (
						<DropdownMenuItem
							key={layer.id}
							onClick={e => e.preventDefault()}
							className="flex flex-col items-start cursor-pointer px-3 py-3 space-y-1"
						>
							<div className="flex items-center justify-between w-full">
								<div className="flex items-center space-x-2">
									{Icon && (
										<div className="text-slate-600 dark:text-slate-400">
											<Icon className="size-4" />
										</div>
									)}
									<Label htmlFor={`layer-${layer.id}`} className="text-sm cursor-pointer font-medium">
										{layer.name}
									</Label>
								</div>
								<Switch
									id={`layer-${layer.id}`}
									checked={layer.visible}
									onCheckedChange={checked => onLayerToggle(layer.id, checked)}
								/>
							</div>
							{layer.description && (
								<p className="text-xs text-slate-500 dark:text-slate-400 ml-6">{layer.description}</p>
							)}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		);
	}

	return (
		<Accordion type="single" collapsible defaultValue="map-layers" className="w-full">
			<AccordionItem value="map-layers">
				<AccordionTrigger className="text-sm font-medium">Map Layers</AccordionTrigger>
				<AccordionContent>
					<div className="space-y-3 pt-2">
						{layers.map(({ Icon, ...layer }) => (
							<div key={layer.id} className="space-y-1">
								<div className="flex items-center justify-between space-x-3">
									<div className="flex items-center space-x-2 flex-1">
										{Icon && (
											<div className="text-slate-600 dark:text-slate-400">
												<Icon className="size-4" />
											</div>
										)}
										<Label
											htmlFor={`mobile-layer-${layer.id}`}
											className="text-sm cursor-pointer font-medium"
										>
											{layer.name}
										</Label>
									</div>
									<Switch
										id={`mobile-layer-${layer.id}`}
										checked={layer.visible}
										onCheckedChange={checked => onLayerToggle(layer.id, checked)}
									/>
								</div>
								{layer.description && (
									<p className="text-xs text-slate-500 dark:text-slate-400 ml-6">
										{layer.description}
									</p>
								)}
							</div>
						))}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
}
